import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, CanDeactivateFn, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { NotifierV2Service } from './notifier-v2.service';
import { MessageDialogService } from '../components/common/message-dialog/services/message-dialog.service';
import { AuthenticationService } from './authentication.service';

/**
 * Functional canDeactivate guard that checks for unsaved changes
 * before allowing navigation away from a route.
 */
export const canDeactivateGuard: CanDeactivateFn<unknown> = (
  component: unknown,
  currentRoute: ActivatedRouteSnapshot,
  currentState: RouterStateSnapshot,
  nextState?: RouterStateSnapshot
): Observable<boolean> | boolean => {
  const notifierService = inject(NotifierV2Service);
  const messageDialogService = inject(MessageDialogService);

  if (!notifierService.canChange()) {
    return new Observable<boolean>((observer) => {
      messageDialogService.openUnSavedChangeConfirmation('').subscribe((res: boolean) => {
        // Timeout used to overcome race condition on subscription of onclose() in messageDialogService
        setTimeout(() => {
          observer.next(res);
          observer.complete();
        }, 1);
      });
    });
  }

  return true;
};

/**
 * Functional canActivate guard that restricts a route to admin users
 * (role === 'Admin' on the current JWT). Redirects non-admins to /home.
 *
 * Returns a UrlTree rather than imperatively calling router.navigate() and
 * returning false — doing both fires two competing navigations at once;
 * returning the UrlTree lets the router itself perform a single, atomic
 * redirect.
 */
export const adminGuard: CanActivateFn = () => {
  const authenticationService = inject(AuthenticationService);
  const router = inject(Router);

  if (authenticationService.isAdmin()) return true;

  return router.createUrlTree(['home']);
};
